<template>
  <v-container>
    <div class="d-flex justify-space-between align-center mt-10 mb-6">
      <div class="font-weight-bold title-page">Representantes legais</div>
      <v-btn outlined color="primary" to="/minha-conta">Voltar</v-btn>
    </div>

    <v-alert type="info" outlined>
      Alterações nos dados ou no documento de um representante precisam de nova aprovação.
      Enquanto a análise estiver pendente, a associação de procurador e a adesão ao plano permanecem bloqueadas.
    </v-alert>

    <v-card v-if="!loading" outlined>
      <v-card-text>
        <v-form ref="form">
          <div v-for="(representative, index) in representatives" :key="representative.key" class="mb-6">
            <div class="d-flex justify-space-between align-center mb-3">
              <div class="subtitle-1 font-weight-bold">Representante {{ index + 1 }}</div>
              <div>
                <v-chip v-if="representative.id" small :color="statusColor(representative.status)" dark class="mr-2">
                  {{ statusLabel(representative.status) }}
                </v-chip>
                <v-btn icon color="error" :disabled="representatives.length === 1" @click="remove(index)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>

            <v-alert v-if="representative.reject_reason" type="error" dense outlined>
              {{ representative.reject_reason }}
            </v-alert>

            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field v-model="representative.name" label="Nome completo" outlined dense
                  :rules="[$rules.required]" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="representative.email" label="E-mail" outlined dense
                  :rules="[$rules.required, $rules.email]" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="representative.registration" label="CPF" outlined dense
                  v-mask="$masks.cpf" :rules="[$rules.required]" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-file-input v-model="representative.file"
                  :label="representative.id ? 'Substituir documento (opcional)' : 'Documento de identificação'"
                  accept="application/pdf,image/png,image/jpeg" outlined dense prepend-icon=""
                  :rules="representative.id ? [] : [$rules.required]" />
              </v-col>
            </v-row>

            <v-btn v-if="representative.documentUrl" small text color="primary"
              :href="representative.documentUrl" target="_blank">
              <v-icon left small>mdi-eye</v-icon>Ver documento atual
            </v-btn>
            <v-divider v-if="index < representatives.length - 1" class="mt-6" />
          </div>
        </v-form>

        <v-btn outlined color="success" @click="add">
          <v-icon left>mdi-plus</v-icon>Adicionar representante
        </v-btn>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text to="/minha-conta">Cancelar</v-btn>
        <v-btn color="primary" :loading="saving" @click="save">Salvar representantes</v-btn>
      </v-card-actions>
    </v-card>

    <div v-else class="text-center py-12"><v-progress-circular indeterminate color="primary" /></div>
  </v-container>
</template>

<script>
import axios from 'axios'

const emptyRepresentative = () => ({
  key: `${Date.now()}-${Math.random()}`,
  id: null,
  name: '',
  email: '',
  registration: '',
  documentUrl: null,
  status: null,
  reject_reason: null,
  file: null
})

export default {
  name: 'UserRepresentativesView',
  data: () => ({ loading: true, saving: false, representatives: [] }),
  created() { this.load() },
  methods: {
    async load() {
      this.loading = true
      try {
        const response = await axios.get('/api/users/info-assicated-lawyer')
        const user = response.data.content || {}
        if (String(user.registration || '').replace(/\D/g, '').length !== 14) {
          this.$dialog.notify.error('Representantes legais estão disponíveis apenas para pessoas jurídicas.')
          this.$router.replace('/minha-conta')
          return
        }
        this.representatives = (user.representatives || []).map(item => ({
          ...emptyRepresentative(),
          ...item,
          registration: String(item.registration || ''),
          file: null
        }))
        if (!this.representatives.length) this.add()
      } catch (error) {
        this.notifyError(error, 'Não foi possível carregar os representantes.')
      } finally { this.loading = false }
    },
    add() { this.representatives.push(emptyRepresentative()) },
    remove(index) { if (this.representatives.length > 1) this.representatives.splice(index, 1) },
    statusLabel(status) {
      return ({ 0: 'Em análise', 1: 'Aprovado', 2: 'Rejeitado' })[Number(status)] || 'Novo'
    },
    statusColor(status) {
      return ({ 0: 'orange', 1: 'success', 2: 'error' })[Number(status)] || 'grey'
    },
    async save() {
      if (!this.$refs.form.validate()) return
      const invalidCpf = this.representatives.some(item => String(item.registration || '').replace(/\D/g, '').length !== 11)
      if (invalidCpf) {
        this.$dialog.notify.error('Informe um CPF válido para todos os representantes.')
        return
      }

      this.saving = true
      try {
        const payload = new FormData()
        const fileIndexes = []
        this.representatives.forEach((item, index) => {
          payload.append(`representatives[${index}][id]`, item.id || '')
          payload.append(`representatives[${index}][name]`, item.name)
          payload.append(`representatives[${index}][email]`, item.email)
          payload.append(`representatives[${index}][registration]`, String(item.registration).replace(/\D/g, ''))
          if (item.file instanceof File) {
            payload.append('representatives_files[]', item.file)
            fileIndexes.push(index)
          }
        })
        fileIndexes.forEach(index => payload.append('representatives_index[]', index))
        await axios.post('/api/users/representative', payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.$dialog.notify.success('Representantes enviados para análise com sucesso!')
        await this.load()
      } catch (error) {
        this.notifyError(error, 'Não foi possível salvar os representantes.')
      } finally { this.saving = false }
    },
    notifyError(error, fallback) {
      const response = error.response && error.response.data
      const message = response && (response.message || (response.content && response.content.error && response.content.error.message))
      this.$dialog.notify.error(message || fallback)
    }
  }
}
</script>
